import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useRange } from '@/lib/useRange';
import { RANGE_LABELS, type RangeKey } from '@/lib/range';
import { Card, CardLabel } from '@/components/ui/card';
import { StatCard } from '@/components/StatCard';
import { DataTable, type Column } from '@/components/DataTable';
import { DonutChart } from '@/components/charts/DonutChart';
import { HorizontalBarChart } from '@/components/charts/HorizontalBarChart';
import { SignupsChart, type SignupsRow } from '@/components/charts/SignupsChart';

function api<T>(path: string): () => Promise<T> {
  return () => apiFetch<T>(path);
}

interface SignupsResponse {
  range: RangeKey;
  baseline: number;
  series: SignupsRow[];
}
interface VerifiedResponse {
  total: number;
  verified: number;
  unverified: number;
  rate: number;
}
interface StatusResponse {
  total: number;
  items: { name: string; value: number }[];
}
interface MixResponse {
  items: { name: string; value: number }[];
}
interface GeoResponse {
  totalDistinct: number;
  totalProfiles: number;
  items: { name: string; value: number }[];
}
interface OptOutsResponse {
  total: number;
  channels: { name: string; value: number }[];
}
interface RecentSignupRow {
  _id: string;
  name: string | null;
  email: string;
  member_number: number | null;
  email_verified: boolean;
  is_archived: boolean;
  is_banned: boolean;
  createdAt: string;
}
interface RecentSignupsResponse {
  limit: number;
  items: RecentSignupRow[];
}

const recentSignupColumns: Column<RecentSignupRow>[] = [
  {
    key: 'member_number',
    header: '#',
    align: 'right',
    width: '60px',
    cell: (r) => (r.member_number ?? '—'),
  },
  {
    key: 'name',
    header: 'Name',
    cell: (r) =>
      r.name?.trim() ? (
        <span className="font-medium">{r.name}</span>
      ) : (
        <span className="text-fg-subtle italic">no name</span>
      ),
  },
  {
    key: 'email',
    header: 'Email',
    cell: (r) => <span className="text-fg-muted">{r.email}</span>,
  },
  {
    key: 'email_verified',
    header: 'Verified',
    align: 'center',
    width: '80px',
    cell: (r) =>
      r.email_verified ? (
        <Check size={16} strokeWidth={2} className="inline text-brand-cyan" />
      ) : (
        <X size={16} strokeWidth={2} className="inline text-fg-subtle" />
      ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '110px',
    cell: (r) => (
      <span
        className={cn(
          'inline-block rounded-md px-2 py-0.5 text-xs font-medium',
          r.is_banned && 'bg-state-danger/15 text-state-danger',
          !r.is_banned && r.is_archived && 'bg-state-warning/15 text-state-warning',
          !r.is_banned && !r.is_archived && 'bg-state-success/10 text-brand-cyan',
        )}
      >
        {r.is_banned ? 'Banned' : r.is_archived ? 'Archived' : 'Active'}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Joined',
    align: 'right',
    width: '170px',
    cell: (r) =>
      new Date(r.createdAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
  },
];

export function Users(): JSX.Element {
  const [range] = useRange();
  const errMsg = (q: { error: unknown }): string | null =>
    q.error ? (q.error as Error).message : null;

  const signups = useQuery({
    queryKey: ['metrics', 'users', 'signups', range],
    queryFn: api<SignupsResponse>(`/api/metrics/users/signups?range=${range}`),
  });
  const verified = useQuery({
    queryKey: ['metrics', 'users', 'verified'],
    queryFn: api<VerifiedResponse>('/api/metrics/users/email-verified-rate'),
  });
  const status = useQuery({
    queryKey: ['metrics', 'users', 'status'],
    queryFn: api<StatusResponse>('/api/metrics/users/status-breakdown'),
  });
  const countries = useQuery({
    queryKey: ['metrics', 'users', 'topCountries'],
    queryFn: api<GeoResponse>('/api/metrics/users/top-countries'),
  });
  const cities = useQuery({
    queryKey: ['metrics', 'users', 'topCities'],
    queryFn: api<GeoResponse>('/api/metrics/users/top-cities'),
  });
  const optOuts = useQuery({
    queryKey: ['metrics', 'users', 'optOuts'],
    queryFn: api<OptOutsResponse>('/api/metrics/users/notification-opt-outs'),
  });
  const recent = useQuery({
    queryKey: ['metrics', 'users', 'recent', 50],
    queryFn: api<RecentSignupsResponse>(
      '/api/metrics/users/recent-signups?limit=50',
    ),
  });

  const verifiedPct = verified.data
    ? `${(verified.data.rate * 100).toFixed(1)}%`
    : undefined;
  const archived = status.data?.items.find((i) => i.name === 'Archived')?.value;
  const banned = status.data?.items.find((i) => i.name === 'Banned')?.value;
  const optOutTotal = optOuts.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="mb-1 text-2xl font-bold">Users</h1>
        <p className="text-sm text-fg-muted">
          Signup growth, verification, geography, and recent activity. Range
          drives the signups chart only — the rest is current-state.
        </p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total"
          value={verified.data?.total}
          loading={verified.isLoading}
          error={errMsg(verified)}
        />
        <StatCard
          label="Email verified"
          value={verifiedPct}
          loading={verified.isLoading}
          error={errMsg(verified)}
          hint={
            verified.data
              ? `${verified.data.verified.toLocaleString()} of ${verified.data.total.toLocaleString()} verified`
              : undefined
          }
        />
        <StatCard
          label="Archived"
          value={archived}
          loading={status.isLoading}
          error={errMsg(status)}
        />
        <StatCard
          label="Banned"
          value={banned}
          loading={status.isLoading}
          error={errMsg(status)}
        />
      </div>

      {/* Signups time-series */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Signups</CardLabel>
          <div className="text-lg font-bold mt-1">
            Daily new users · Cumulative total · {RANGE_LABELS[range]}
          </div>
        </div>
        {signups.isLoading ? (
          <div className="h-[280px] animate-pulse rounded-md bg-surface-divider/30" />
        ) : signups.error ? (
          <div className="text-sm text-state-danger">{errMsg(signups)}</div>
        ) : (
          <SignupsChart data={signups.data?.series ?? []} />
        )}
      </Card>

      {/* Status donut + geography */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Status</CardLabel>
            <div className="text-lg font-bold mt-1">User status breakdown</div>
          </div>
          {status.isLoading ? (
            <div className="h-[240px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : status.error ? (
            <div className="text-sm text-state-danger">{errMsg(status)}</div>
          ) : (
            <DonutChart data={status.data?.items ?? []} centerLabel="Users" />
          )}
        </Card>

        <Card className="flex flex-col gap-4">
          <div className="flex items-end justify-between">
            <div>
              <CardLabel>Top countries</CardLabel>
              <div className="text-lg font-bold mt-1">
                {countries.data
                  ? `${countries.data.totalDistinct} distinct · ${countries.data.totalProfiles} of ${verified.data?.total ?? '—'} profiles`
                  : 'Where users live'}
              </div>
            </div>
          </div>
          {countries.isLoading ? (
            <div className="h-[260px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : countries.error ? (
            <div className="text-sm text-state-danger">{errMsg(countries)}</div>
          ) : (
            <HorizontalBarChart
              data={(countries.data?.items ?? []).slice(0, 10)}
              height={260}
              yWidth={160}
            />
          )}
        </Card>

        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Top cities</CardLabel>
            <div className="text-lg font-bold mt-1">
              {cities.data
                ? `${cities.data.totalDistinct} distinct · ${cities.data.totalProfiles} of ${verified.data?.total ?? '—'} profiles`
                : 'Where users live'}
            </div>
          </div>
          {cities.isLoading ? (
            <div className="h-[260px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : cities.error ? (
            <div className="text-sm text-state-danger">{errMsg(cities)}</div>
          ) : (
            <HorizontalBarChart
              data={(cities.data?.items ?? []).slice(0, 10)}
              height={260}
              yWidth={160}
            />
          )}
        </Card>
      </div>

      {/* Notification opt-outs */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Notification opt-outs</CardLabel>
          <div className="text-lg font-bold mt-1">
            Profiles that have disabled each channel
            {optOutTotal > 0 ? ` · of ${optOutTotal.toLocaleString()}` : ''}
          </div>
        </div>
        {optOuts.isLoading ? (
          <div className="h-[80px] animate-pulse rounded-md bg-surface-divider/30" />
        ) : optOuts.error ? (
          <div className="text-sm text-state-danger">{errMsg(optOuts)}</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {(optOuts.data?.channels ?? []).map((ch) => {
              const pct =
                optOutTotal === 0 ? 0 : (ch.value / optOutTotal) * 100;
              return (
                <div key={ch.name} className="flex flex-col gap-1.5">
                  <div className="text-xs font-bold uppercase tracking-wide text-fg-muted">
                    {ch.name}
                  </div>
                  <div className="text-xl font-extrabold tabular-nums">
                    {ch.value.toLocaleString()}
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-surface-divider/30 overflow-hidden">
                    <div
                      className="h-full bg-brand-red"
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                  <div className="text-xs text-fg-subtle">
                    {pct.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Recent signups */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Recent signups</CardLabel>
          <div className="text-lg font-bold mt-1">Most recent 50</div>
        </div>
        <DataTable
          columns={recentSignupColumns}
          rows={recent.data?.items}
          loading={recent.isLoading}
          error={errMsg(recent)}
          rowKey={(r) => r._id}
        />
      </Card>
    </div>
  );
}
